/**
 * External dependencies
 */
import React, { useState } from "react";

import { map } from "lodash";
import classnames from "classnames";
import PopoverMenu from "components/popover/menu";
import { ButtonV2 } from "../button-v2";
import { VariantProps, cva } from "class-variance-authority";
import "./styles.scss";
import { twMerge } from "tailwind-merge";
import { IoMdArrowDropdown } from "react-icons/io";
import { Text } from "components/text";

export type DropdownLinkItem = {
  key: string;
  label: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick: Function;
};

export const activatorStyles = cva(
  "relative p-2 border flex gap-3 cursor-pointer rounded-lg transition-all border-2 border-solid border-gray-200 bg-white justify-center items-center hover:border-blue-400 hover:bg-blue-50",
  {
    variants: {
      size: {
        md: "w-48",
        sm: "w-36",
        xs: "w-24",
      },
      elevation: {
        1: "shadow-sm",
        2: "shadow-md",
      },
      compact: {
        true: "p-1 px-2 ",
      },
    },
    compoundVariants: [],
    defaultVariants: {
      elevation: 1,

      compact: true,
    },
  },
);

export type ActivatorStyleProps = VariantProps<typeof activatorStyles>;

export const DropdownLinkV2 = ({
  disabled,
  label,
  title,
  onOuterAction,
  onClose,
  items,
  showArrow = true,
  className,
  buttonRender,
  children,
  icon,
  activatorProps,
}: {
  disabled?: boolean;
  label?: string;
  title?: string | React.ReactNode;
  onOuterAction?: Function;
  onClose?: Function;
  items: DropdownLinkItem[];
  showArrow?: boolean;
  className?: string;
  buttonRender?: Function;
  children?: Function;
  icon?: any;
  activatorProps?: ActivatorStyleProps;
}) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const handleClick = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const classes = classnames({
    "is-open": isMenuVisible,
    disabled,
  });

  const handleClose = () => {
    setIsMenuVisible(false);
    onClose && onClose();
  };

  return (
    <div className={classes}>
      <PopoverMenu
        className={classnames("dropdown-link", className)}
        enterExitTransitionDurationMs={50}
        onOuterAction={() =>
          onOuterAction ? onOuterAction(handleClose) : handleClose()
        }
        isOpen={isMenuVisible}
        showArrow={showArrow}
        content={
          <div className="p-3">
            {map(items, (item) => {
              let c = classnames("dropdown-link__item", {
                active: item.isActive,
              });
              return (
                <div key={item.key} className="dropdown-link__item-wrapper">
                  <ButtonV2
                    className={c}
                    disabled={disabled || item.disabled}
                    onClick={async () => {
                      setIsMenuVisible(false);
                      await new Promise((r) => setTimeout(r, 100)); // stop the render from blinking
                      item.onClick(item);
                    }}
                  >
                    <div>
                      <Text variant="bodyMd">{item.label}</Text>
                    </div>
                  </ButtonV2>
                </div>
              );
            })}
            {children && children({ close: handleClose })}
          </div>
        }
      >
        {(buttonRender && buttonRender({ handleClick, title, label })) || (
          <div
            onClick={handleClick}
            className={twMerge(
              activatorStyles({
                ...activatorProps,
              }),
            )}
          >
            {icon}
            <div className="flex hover:text-[#4394e5]">
              {typeof title !== "string" && title ? (
                title
              ) : (
                <Text variant="headingXxs">{title || label || "-"}</Text>
              )}
            </div>
            {showArrow && (
              <div>
                <IoMdArrowDropdown />
              </div>
            )}
          </div>
        )}
      </PopoverMenu>
    </div>
  );
};
